const CONSENT_BANNER_SELECTOR = '.dg-consent-banner, dg-consent-banner'

function preserveBannerTitleBrandCasing(root: ParentNode): void {
	const title = root.querySelector('.dg-text-title') as HTMLElement | null
	if (!title) return

	const currentTitle = title.textContent?.trim()
	if (currentTitle === 'Pvolve Values Your Privacy') {
		title.textContent = 'PVOLVE Values Your Privacy'
	}
}

function assignCookieBannerParts(): boolean {
	const banner = document.querySelector(CONSENT_BANNER_SELECTOR)
	if (!banner) return false
	const bannerEl = banner as HTMLElement

	const shadow = banner.shadowRoot
	if (!shadow) return false

	const app = shadow.querySelector('.dg-app') as HTMLElement | null
	if (!app) return false

	if (bannerEl.dataset.pvolveDgPartsBound === 'true') return true

	app.setAttribute('part', 'dg-app')

	const partMap: Record<string, string> = {
		'.dg-text-body': 'dg-body',
		'.dg-links': 'dg-links',
		'.accept_all': 'dg-accept-button',
		'.reject_all': 'dg-reject-button',
		'.open_layer': 'dg-manage-button',
	}

	for (const child of Array.from(app.children)) {
		const el = child as HTMLElement

		for (const [selector, part] of Object.entries(partMap)) {
			if (el.matches(selector)) {
				el.setAttribute('part', part)
			}
		}

		if (el.tagName === 'DIV' && !el.className.includes('dg-links')) {
			el.setAttribute('part', 'dg-header-wrapper')
		}
	}

	const actionButtons = app.querySelectorAll(
		'.accept_all, .reject_all, .open_layer'
	)
	if (actionButtons.length && !app.querySelector('[part="dg-buttons"]')) {
		const wrapper = document.createElement('div')
		wrapper.setAttribute('part', 'dg-buttons')
		const firstButton = actionButtons[0] as HTMLElement
		firstButton.parentNode!.insertBefore(wrapper, firstButton)
		actionButtons.forEach((btn) => wrapper.appendChild(btn))
	}

	const languageDropdown = app.querySelector('.dg-main-language-dropdown') as HTMLElement | null
	if (languageDropdown) {
		languageDropdown.setAttribute('part', 'dg-language-dropdown')
	}

	const customButton = app.querySelector('.dg-button.custom') as HTMLElement | null
	if (customButton) {
		customButton.hidden = true
		customButton.setAttribute('aria-hidden', 'true')
		customButton.setAttribute('tabindex', '-1')
	}

	const acceptButton = app.querySelector('.accept_all') as HTMLElement | null
	const rejectButton = app.querySelector('.reject_all') as HTMLElement | null
	const manageButton = app.querySelector('.open_layer') as HTMLElement | null
	acceptButton?.setAttribute('aria-label', 'Accept')
	rejectButton?.setAttribute('aria-label', 'Reject')
	manageButton?.setAttribute('aria-label', 'Manage Preferences')

	const policyDescriptions = app.querySelectorAll('.dg-main-content-policy-option-description p')
	policyDescriptions.forEach((p) => {
		;(p as HTMLElement).setAttribute('part', 'dg-policy-text')
	})

	const links = app.querySelector('.dg-links')
	if (links) {
		for (const child of Array.from(links.children)) {
			const el = child as HTMLElement
			if (el.matches('.dg-link')) {
				el.setAttribute('part', 'dg-link')
			}
		}
	}

	const header = app.querySelector('.dg-header') as HTMLElement | null
	if (header) {
		preserveBannerTitleBrandCasing(header)
		header.setAttribute('part', 'dg-header')
		const headerPartMap: Record<string, string> = {
			'.dg-text-title': 'dg-title',
			'.dg-header-buttons': 'dg-header-buttons',
		}

		for (const child of Array.from(header.children)) {
			const el = child as HTMLElement

			for (const [selector, part] of Object.entries(headerPartMap)) {
				if (el.matches(selector)) {
					el.setAttribute('part', part)
				}
			}
		}

		// Map dismiss button children inside dg-header-buttons
		const headerButtons = header.querySelector(
			'.dg-header-buttons'
		) as HTMLElement | null
		if (headerButtons) {
			for (const child of Array.from(headerButtons.children)) {
				const el = child as HTMLElement
				if (
					el.matches(
						'[aria-label*="Expand"], [aria-label*="expand"]'
					)
				) {
					el.setAttribute('part', 'dg-expand-button')
				} else if (
					el.tagName === 'BUTTON' ||
					el.matches(
						'.dg-close, [aria-label*="close"], [aria-label*="dismiss"], [aria-label*="Close"]'
					)
				) {
					el.setAttribute('part', 'dg-dismiss-button')
				}
			}
		}
	}

	bindDismissHandler(shadow)

	bannerEl.dataset.pvolveDgPartsBound = 'true'

	return true
}

function bindDismissHandler(shadow: ShadowRoot): void {
	const dismissBtn = shadow.querySelector(
		'[part="dg-dismiss-button"]'
	) as HTMLElement | null
	if (dismissBtn && !dismissBtn.dataset.pvolveHandlerBound) {
		dismissBtn.hidden = true
		dismissBtn.setAttribute('aria-hidden', 'true')
		dismissBtn.setAttribute('tabindex', '-1')
		dismissBtn.addEventListener('click', (event) => {
			event.preventDefault()
			event.stopPropagation()
			event.stopImmediatePropagation()
		}, true)
		dismissBtn.dataset.pvolveHandlerBound = 'true'
	}
}

function observeShadowRoot(): void {
	const banner = document.querySelector(CONSENT_BANNER_SELECTOR)
	if (!banner?.shadowRoot) return

	let rebindRAF: number | null = null
	const shadowObserver = new MutationObserver(() => {
		if (rebindRAF) cancelAnimationFrame(rebindRAF)
		rebindRAF = requestAnimationFrame(() => {
			const bannerEl = banner as HTMLElement
			delete bannerEl.dataset.pvolveDgPartsBound
			assignCookieBannerParts()
			rebindRAF = null
		})
	})

	shadowObserver.observe(banner.shadowRoot, {
		childList: true,
		subtree: true,
	})
}

export function initCookieBannerParts(): void {
	if (!assignCookieBannerParts()) {
		const observer = new MutationObserver(() => {
			if (assignCookieBannerParts()) {
				observer.disconnect()
				observeShadowRoot()
			}
		})

		observer.observe(document.body, { childList: true, subtree: true })
		return
	}

	observeShadowRoot()
}
